import axios from 'axios';
import { Auth } from 'aws-amplify';
import { localRedirect } from 'utils';

let isRefreshing = false;
let refreshSubscribers = [];

export class ResponseError extends Error {
  public response: Response;

  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const supersetBaseUrl = process.env.REACT_APP_SUPERSET_BASE_URL || '';
const api = axios.create({
  baseURL: baseUrl
});

const logout = () => {
  Auth.signOut();
  localRedirect('/login');
};

api.interceptors.request.use(async config => {
  if (!config.url?.startsWith(supersetBaseUrl)) {
    try {
      const session = await Auth.currentSession();
      if (session) {
        const accessToken = session.getIdToken().getJwtToken();
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }
      } else {
        logout();
        return Promise.reject(new axios.CanceledError('Cancelled'));
      }
    } catch (e) {
      console.error('Error fetching token:', e);
      logout();
      return Promise.reject(new axios.CanceledError('Cancelled'));
    }
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (error.response && error.response.status === 401) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newTokens = await Auth.currentSession();
          api.defaults.headers.common['Authorization'] = `Bearer ${newTokens.getIdToken().getJwtToken()}`;
          // Retry the original request
          originalRequest.headers['Authorization'] = `Bearer ${newTokens.getIdToken().getJwtToken()}`;
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Error refreshing token:', refreshError);
          logout();
        } finally {
          isRefreshing = false;
          refreshSubscribers = [];
        }
      } else {
        return new Promise(resolve => {
          refreshSubscribers.push((token: any) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }
    }
    return Promise.reject(error.response?.data || error);
  }
);

export default api;
